<template>
<section class="wind-profile-chart-wrapper">
  <section
    :style="{
      width: `${chartWidth}px`,
      height: `${chartHeight}px`
    }"
    class="wind-profile-chart-inner"
    @mousedown="onChartMouseDown"
    @mousemove="onChartMouseMove"
    @mouseup="onChartMouseUp"
    @mouseout="onChartMouseOut"
    @mousewheel="onChartMouseWheel"
    @dblclick="onChartDBLClick"
    @contextmenu.prevent="onChartContextMenu"
  >
    <canvas ref="wind-profile-chart" />
    <canvas ref="tooltip-canvas" class="tooltip-canvas" />
    <canvas ref="selected-canvas" class="selected-canvas" />
  </section>

  <a class="download-btn" @click="onDownloadBtnClick">
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="14"
      height="14"
      style="fill:currentColor;"
      viewBox="0 0 1024 1024"
      version="1.1"
    >
      <path d="M908.304 900.882H113.648c-11.895 0-21.535 9.64-21.535 21.535 0 11.896 9.64 21.536 21.535 21.536h794.655c11.896 0 21.536-9.64 21.536-21.536 0.001-11.894-9.64-21.535-21.535-21.535z m-412.575-68.92a21.106 21.106 0 0 0 3.315 2.71c0.493 0.328 1.036 0.555 1.553 0.845 0.716 0.387 1.397 0.818 2.152 1.131 0.698 0.282 1.412 0.455 2.128 0.661 0.63 0.18 1.232 0.43 1.89 0.559 1.393 0.267 2.797 0.422 4.21 0.422 1.413 0 2.825-0.155 4.215-0.422 0.653-0.136 1.256-0.379 1.894-0.559 0.714-0.206 1.438-0.379 2.127-0.661 0.757-0.312 1.446-0.744 2.162-1.138 0.508-0.283 1.042-0.51 1.544-0.838a22.044 22.044 0 0 0 3.315-2.71l326.168-326.17c8.416-8.416 8.416-22.04 0-30.449-8.409-8.417-22.053-8.417-30.454 0L532.512 764.777V136.375c0-11.895-9.64-21.535-21.535-21.535-11.896 0-21.537 9.64-21.537 21.535v628.394L200.005 475.335c-8.408-8.417-22.052-8.417-30.452 0-8.417 8.417-8.417 22.04 0 30.45L495.729 831.96z m0 0"/>
    </svg>
  </a>

  <section
    :style="{
        top: `${tooltip.position.y}px`,
        left: `${tooltip.position.x}px`,
        visibility: tooltip.visibility
      }"
    class="tooltip"
  >
    <div>
      <span :style="{
        'background-color': `rgb(${tooltip.legendColor.join(',')})`
      }" class="legend-marker"></span>
      <span>{{tooltip.dataName}}</span>
    </div>
    <div
      v-for="(tooltipData, index) in tooltip.data"
      :key="index"
    >
      <span class="tooltip-label">{{tooltipData.label}}: </span>
      <span class="tooltip-value">{{tooltipData.value}}</span>
    </div>
  </section>
</section>
</template>

<script>
import merge from 'deepmerge'

import {
  WIND_SPEED_SIZE,
  WIND_SPEED_CENTER,
  windProfileConfigs,
  windProfileTemplate,
  getSpeedLevel
} from './windProfileTemplate'
import {
  WIND_PROFILE_CHART_DATA_CANCEL_SELECT,
  WIND_PROFILE_CHART_DATA_SELECTED,
  WIND_PROFILE_CHART_DESTROY,
  WIND_PROFILE_CHART_MOVED,
  WIND_PROFILE_CHART_READY
} from './EventKeys'
import {
  AXIS_FONT, BORDER_STYLE,
  FONT_SIZE, LINE_WIDTH,
  MAIN_AXIS_SIZE,
  SPLIT_LINE_STYLE,
  SUB_AXIS_SIZE,
  TEXT_FILL_STYLE,
  VALUE_INTERVAL, WIND_PROFILE_TOOLTIP_BORDER_STYLE
} from '../chartStyle'

const BASE_TOP = 2
const TOP_GAP = FONT_SIZE + 8

const MIN_WIDTH_SCALE = 50
// const MIN_HEIGHT_SCALE = 18
const MIN_HEIGHT_SCALE = WIND_SPEED_SIZE[1]

const LEGEND_UNIT_HEIGHT = FONT_SIZE + 5
const LEGEND_WIDTH = 55
const LEGEND_HEIGHT = LEGEND_UNIT_HEIGHT + windProfileConfigs.filter(({isLegend}) => isLegend).length * MIN_HEIGHT_SCALE
const LEGEND_ANGLE = 90

const X_CHART_HEIGHT = 215
const X_AXIS_HEIGHT = 35
const BOTTOM_CHART_HEIGHT = X_AXIS_HEIGHT + X_CHART_HEIGHT
const Y_CHART_WIDTH = 215

const SIDE_CHART_AXIS_GAP = 5

const MAX_SPEED = windProfileConfigs.find(windProfileConfig => windProfileConfig.isMaxVal).speed

export default {
  name: 'WindProfileChart',
  data () {
    return {
      yAxisWidth: 51,

      displaySpectraWidth: 0,
      displaySpectraHeight: 0,

      startX: 0,
      startY: 0,

      xAxisDataLen: 0,
      yAxisDataLen: 0,

      widthScale: WIND_SPEED_SIZE[0],
      heightScale: WIND_SPEED_SIZE[1],

      tooltip: {
        visibility: 'hidden',
        position: {
          x: 0,
          y: 0
        },
        data: [],
        dataName: '',
        legendColor: []
      }
    }
  },
  computed: {
    rightChartWidth () {
      return this.yAxisWidth + Y_CHART_WIDTH
    },
    chartWidth () {
      return LEGEND_WIDTH + this.displaySpectraWidth + this.rightChartWidth
    },
    chartHeight () {
      return TOP_GAP + this.displaySpectraHeight + BOTTOM_CHART_HEIGHT
    },
    cacheSpectraWidth () {
      return this.xAxisDataLen * this.widthScale
    },
    cacheSpectraHeight () {
      return this.yAxisDataLen * this.heightScale
    },
    cacheChartWidth () {
      return LEGEND_WIDTH + this.cacheSpectraWidth + this.rightChartWidth
    },
    cacheChartHeight () {
      return TOP_GAP + this.cacheSpectraHeight + BOTTOM_CHART_HEIGHT
    }
  },
  methods: {
    /**
     * 对外接口, 设置雷达图表的显示参数
     * @param chartOpt<Object>  {
     *   tooltip<Object>: {
     *     xAxisFormatter<Function>:          显示tooltip时的格式化xAxis值的函数
     *     yAxisFormatter<Function>:          显示tooltip时的格式化yAxis值的函数
     *     speedFormatter<Function>:          显示tooltip时的格式化风速值的函数
     *     angleFormatter<Function>:          显示tooltip时的格式化风向值的函数
     *   },
     *   xAxis<Object>: x轴坐标配置参数 {
     *     fit<Boolean>:                      是否自适应
     *     step<Number>:                      间隔
     *     data<Array<Number, String, Date>>: x轴坐标数据
     *     formatter<Function>:               x轴坐标显示标签的格式化函数
     *   },
     *   yAxis<Object>: y轴坐标配置参数 {
     *     name<String>:                      y轴坐标名称
     *     unit<String>:                      y轴单位
     *     fit<Boolean>:                      是否自适应
     *     step<Number>:                      间隔
     *     data<Array<Number, String, Date>>: y轴坐标数据
     *     formatter<Function>:               y轴坐标显示标签的格式化函数
     *   },
     *   series<Object>: 数据配置 {
     *     name<String>:                      序列名称
     *     dataArray<Array<Array<Number>>>:   子数组每一个值都为yAxis上的data中对应的值
     *   }
     * }
     * @param clear 是否需要清除
     */
    setOption (chartOpt, clear) {
      if (clear) {
        this.clearDataOption()
      }

      this.chartOpt = merge(this.chartOpt || {}, chartOpt)
      this.tooltip.dataName = this.chartOpt.series.name
      this.setChartSize()

      console.time('wind profile chart')
      this.drawChart()
      console.timeEnd('wind profile chart')
    },

    /**
     * 对外接口, 清空风廓线雷达图表
     */
    clear () {
      this.clearAllCanvas()
      this.clearDataOption()
    },

    /**
     * 对外接口, 重置风廓线雷达大小
     */
    resize () {
      this.setChartSize()
      this.drawChart()
      this.moveChartByXY(this.startX, this.startY)
    },

    /**
     * 对外接口, 选中图表上的数据
     * @param xAxisIndex<Number>  x数据坐标序号
     * @param yAxisIndex<Number>  y数据坐标序号
     */
    selectData (xAxisIndex, yAxisIndex) {
      let {
        chartOpt: {
          xAxis: {
            data: xAxisData
          },
          yAxis: {
            data: yAxisData
          },
          series: {
            data: seriesData
          }
        }
      } = this

      let centerX = xAxisIndex * this.widthScale - this.displaySpectraWidth / 2
      let centerY = yAxisIndex * this.heightScale - this.displaySpectraHeight / 2
      this.moveChartByXY(centerX, centerY)
      this.drawSelected({
        area: 3,
        x: xAxisIndex,
        y: yAxisIndex,
        xData: xAxisData[xAxisIndex],
        yData: yAxisData[yAxisIndex],
        value: Array.isArray(seriesData[xAxisIndex]) && Number.isFinite(seriesData[xAxisIndex][yAxisIndex]) ? Number(seriesData[xAxisIndex][yAxisIndex].toFixed(3)) : null,
        selectedData: {
          xValues: seriesData.map(values => Array.isArray(values) ? values[yAxisIndex] : []),
          yValues: seriesData[xAxisIndex]
        }
      })
    },

    /**
     * 对外接口, 在已有的数据之前插入数据
     * @param chartDataOpt {
     *   // x轴坐标配置参数
     *   xAxis: {
     *     data: Array<Number, String, Date>
     *   },
     *   // 数据配置
     *   series: [{
     *     // 数据, 子数组每一个值都为yAxis上的data中对应的值
     *     data: values: Array<Array<Number>>
     *   }]
     * }
     */
    insertData (chartDataOpt) {
      this.addData(chartDataOpt, 0)
    },

    /**
     * 对外接口, 在已有的数据之后追加数据
     * @param chartDataOpt {
     *   // x轴坐标配置参数
     *   xAxis: {
     *     // x轴坐标数据
     *     data: Array<Number, String, Date>
     *   },
     *   // 数据配置
     *   series: [{
     *     // 数据, 子数组每一个值都为yAxis上的data中对应的值
     *     data: values: Array<Array<Number>>
     *   }]
     * }
     */
    appendData (chartDataOpt) {
      this.addData(chartDataOpt, 1)
    },

    /**
     * 内部使用, 清空所有画布
     */
    clearAllCanvas () {
      let canvas = [
        this.cacheChart,
        this.cacheXTrendChart,
        this.cacheYTrendChart,
        this.cacheSelectedChart,
        this.getDisplayChart(),
        this.getTooltipLineCanvas(),
        this.getSelectedCanvas()
      ]

      canvas.forEach(canvas => {
        canvas.width = canvas.height = 0
      })
    },

    /**
     * 内部使用, 清除数据
     */
    clearDataOption () {
      this.chartOpt = null
      this.selectedInfo = null
      this.startX = this.startY = 0
    },

    /**
     * 内部使用, 设置图表尺寸
     */
    setChartSize () {
      let {chartOpt} = this

      if (!chartOpt) {
        return
      }

      let thisRootEl = this.$el
      let displayCanvas = this.getDisplayChart()
      let rootWidth = thisRootEl.offsetWidth
      let rootHeight = thisRootEl.offsetHeight

      let {xAxis, yAxis} = chartOpt
      let xAxisData = xAxis.data
      let yAxisData = yAxis.data
      let xAxisDataLen = this.xAxisDataLen = xAxisData.length
      let yAxisDataLen = this.yAxisDataLen = yAxisData.length
      let ctx = displayCanvas.getContext('2d')

      ctx.font = AXIS_FONT
      let maxYAxisLabelWidth = this.maxYAxisLabelWidth = Math.ceil(Math.max(...yAxisData.map(num => ctx.measureText(num).width)))
      this.yAxisWidth = Math.min((maxYAxisLabelWidth - ((maxYAxisLabelWidth & 1) ^ 1)) + (MAIN_AXIS_SIZE + 5) * 2)

      let displaySpectraWidth = this.displaySpectraWidth = rootWidth - LEGEND_WIDTH - this.rightChartWidth
      let displaySpectraHeight = this.displaySpectraHeight = Math.max(rootHeight - TOP_GAP - BOTTOM_CHART_HEIGHT, LEGEND_HEIGHT)

      this.widthScale = this.getCacheSpectraScale(xAxis.fit, displaySpectraWidth, xAxisDataLen, MIN_WIDTH_SCALE)
      this.heightScale = this.getCacheSpectraScale(yAxis.fit, displaySpectraHeight, yAxisDataLen, MIN_HEIGHT_SCALE)
    },

    /**
     * 获取图表比例
     * @param fit<Boolean>                是否自适应
     * @param displaySpectraSize<Number>  显示图谱区域尺寸
     * @param dataLen<Number>             数据长度
     * @param minScale<Number>            最小缩放倍数
     */
    getCacheSpectraScale (fit, displaySpectraSize, dataLen, minScale) {
      if (fit && dataLen > 0) {
        return Math.max(Math.ceil(displaySpectraSize / dataLen), minScale)
      }

      return minScale
    },

    /**
     * 开始画图表
     */
    drawChart () {
      let {cacheChart, cacheChartWidth, cacheChartHeight} = this
      let cacheCtx = cacheChart.getContext('2d')
      cacheChart.width = cacheChartWidth
      cacheChart.height = cacheChartHeight
      cacheCtx.clearRect(0, 0, cacheChartWidth, cacheChartHeight)

      this.buildCache(cacheCtx)
      this.drawDisplayChart()
    },

    /**
     * 建立缓冲区
     * @param cacheCtx  缓冲区画笔
     */
    buildCache (cacheCtx) {
      this.buildLegendCache(cacheCtx)
      this.buildSpectraCache(cacheCtx)
      this.buildSelectedDataCache(this.selectedInfo, cacheCtx)
    },

    /**
     * 创建图例缓冲区
     * @param cacheCtx<CanvasRenderingContext2D>  缓冲区画布的画笔
     */
    buildLegendCache (cacheCtx) {
      let reverseWindProfileConfigs = [...windProfileConfigs].reverse()
      let [width, height] = WIND_SPEED_SIZE
      let halfHeight = WIND_SPEED_CENTER[1]
      let top = 0
      let {heightScale} = this
      let centerX = Math.round(LEGEND_WIDTH / 2)
      let reverseWindProfileConfigsLen = reverseWindProfileConfigs.length - 1
      let legendTop = TOP_GAP + LEGEND_UNIT_HEIGHT
      cacheCtx.fillStyle = TEXT_FILL_STYLE
      cacheCtx.font = AXIS_FONT
      cacheCtx.textAlign = 'center'
      cacheCtx.textBaseline = 'top'
      cacheCtx.fillText('图例', centerX, BASE_TOP)
      cacheCtx.fillText('(m/s)', centerX, BASE_TOP + LEGEND_UNIT_HEIGHT)

      cacheCtx.textAlign = 'right'
      cacheCtx.textBaseline = 'middle'

      reverseWindProfileConfigs.forEach(({speed, isLegend, i}, index) => {
        if (!isLegend) {
          top++
          return
        }

        let y = legendTop + (index - top) * heightScale

        cacheCtx.drawImage(
          windProfileTemplate,
          width * LEGEND_ANGLE, (reverseWindProfileConfigsLen - index) * height,
          width, height,
          0, y,
          width, height
        )

        cacheCtx.fillText(speed, LEGEND_WIDTH - 5, y + halfHeight + 1)
      })

      cacheCtx = width = height = null
    },

    /**
     * 创建风郭线雷达图谱缓冲区
     * @param cacheCtx<CanvasRenderingContext2D>  缓冲区画布的画笔
     */
    buildSpectraCache (cacheCtx) {
      let {chartOpt, widthScale, heightScale} = this
      let {
        series: {
          data: seriesData
        }
      } = chartOpt
      let [baseLeft, baseTop] = [LEGEND_WIDTH + Math.round((widthScale - WIND_SPEED_SIZE[0]) / 2), TOP_GAP + Math.round((heightScale - WIND_SPEED_SIZE[1]) / 2)]
      let [width, height] = WIND_SPEED_SIZE

      seriesData.forEach((yValues, seriesDataIndex) => {
        let x = baseLeft + seriesDataIndex * widthScale

        yValues.forEach(([speed, angle], yValuesIndex) => {
          if (!Number.isFinite(speed) || !Number.isFinite(angle)) {
            return
          }

          let y = baseTop + yValuesIndex * heightScale

          let speedLevel = getSpeedLevel(speed)
          angle = Math.round(angle)

          cacheCtx.drawImage(
            windProfileTemplate,
            angle * width, speedLevel * height,
            width, height,
            x, y,
            width, height
          )
        })
      })
    },

    clearAxisCache () {
      let {cacheChart, cacheChartWidth, cacheChartHeight, rightChartWidth} = this
      let cacheCtx = cacheChart.getContext('2d')
      cacheCtx.clearRect(cacheChartWidth - rightChartWidth, 0, rightChartWidth, cacheChartHeight)
      cacheCtx.clearRect(0, cacheChartHeight - BOTTOM_CHART_HEIGHT, cacheChartWidth, cacheChartHeight)
      return cacheCtx
    },

    /**
     * 创建坐标刻度线缓冲区
     * @param cacheCtx<CanvasRenderingContext2D>  缓冲区画布的画笔
     */
    buildAxisCache (cacheCtx) {
      let {cacheSpectraWidth, cacheSpectraHeight} = this
      cacheCtx = cacheCtx || this.clearAxisCache()

      // yChart坐标轴
      let yChartSize = [Y_CHART_WIDTH, cacheSpectraHeight]
      let yAxisTickLeft = LEGEND_WIDTH + cacheSpectraWidth
      let yAxisTickRight = this.cacheChartWidth - yChartSize[0]
      let xAxisTickImgData = this.buildAxisLineCache(
        [yAxisTickRight, TOP_GAP],
        yChartSize,
        1,
        cacheCtx
      )
      cacheCtx.putImageData(
        this.getLineImageData(cacheSpectraHeight, 1, BORDER_STYLE),
        yAxisTickLeft, TOP_GAP
      )

      // xChart坐标轴
      let xChartSize = [cacheSpectraWidth, X_CHART_HEIGHT]
      let xAxisTickTop = TOP_GAP + cacheSpectraHeight
      let xAxisTickBottom = this.cacheChartHeight - xChartSize[1]
      let yAxisTickImgData = this.buildAxisLineCache(
        [LEGEND_WIDTH, xAxisTickBottom],
        xChartSize,
        0,
        cacheCtx
      )
      cacheCtx.putImageData(
        this.getLineImageData(cacheSpectraWidth, 0, BORDER_STYLE),
        LEGEND_WIDTH, xAxisTickTop
      )

      cacheCtx.fillStyle = TEXT_FILL_STYLE
      cacheCtx.font = AXIS_FONT
      cacheCtx.textAlign = 'center'
      cacheCtx.textBaseline = 'middle'

      let {chartOpt, widthScale, heightScale} = this

      // 绘制图谱X轴坐标刻度和标签
      let {xAxis: {data: xAxisData, interval: xAxisInterval, formatter: xAxisFormatter}} = chartOpt
      let offsetX = LEGEND_WIDTH + Math.round(widthScale / 2)
      let xAxisLabelMiddle = xAxisTickTop + Math.round(X_AXIS_HEIGHT / 2)
      xAxisTickBottom -= SUB_AXIS_SIZE

      xAxisData.forEach((xAxisVal, index) => {
        let x = index * widthScale + offsetX
        cacheCtx.putImageData(
          xAxisTickImgData,
          x, xAxisTickTop
        )
        cacheCtx.putImageData(
          xAxisTickImgData,
          x, xAxisTickBottom
        )

        if ((index % xAxisInterval) === 0) {
          cacheCtx.putImageData(
            xAxisTickImgData,
            x, xAxisTickTop + SUB_AXIS_SIZE
          )
          cacheCtx.putImageData(
            xAxisTickImgData,
            x, xAxisTickBottom - SUB_AXIS_SIZE
          )
          cacheCtx.fillText(xAxisFormatter(xAxisVal), x, xAxisLabelMiddle)
        }
      })

      // 绘制图谱y轴坐标刻度和标签
      let {yAxis: {data: yAxisData, interval: yAxisInterval}} = chartOpt
      let offsetY = TOP_GAP + Math.round(heightScale / 2)
      let yAxisLabelCenter = yAxisTickLeft + Math.round(this.yAxisWidth / 2)
      yAxisTickRight -= SUB_AXIS_SIZE

      yAxisData.forEach((yAxisVal, index) => {
        let y = index * heightScale + offsetY
        cacheCtx.putImageData(
          yAxisTickImgData,
          yAxisTickLeft, y
        )
        cacheCtx.putImageData(
          yAxisTickImgData,
          yAxisTickRight, y
        )

        if ((index % yAxisInterval) === 0) {
          cacheCtx.fillText(yAxisVal, yAxisLabelCenter, y + 1)
        }
      })

      // 绘制xChart的刻度值
      this.drawAxisLabel(cacheCtx, [yAxisTickLeft, xAxisTickBottom], xChartSize[1], ['left', 'top', 'middle'], 1)
      // 绘制yChart的刻度值
      this.drawAxisLabel(cacheCtx, [yAxisTickRight, xAxisTickTop], yChartSize[0], ['top', 'left', 'center'], 0)

      return cacheCtx
    },

    /**
     * 获取xy坐标线
     * @param position<Array<Number>>             起始位置
     * @param size<Array<Number>>                 尺寸
     * @param direction<Number>                   方向: 0.横向  1.纵向
     * @param cacheCtx<CanvasRenderingContext2D>  缓冲区画布的画笔
     */
    buildAxisLineCache (position, size, direction, cacheCtx) {
      let nextDirection = direction ^ 1
      let len = size[direction]
      let nextLen = size[nextDirection]
      let [x, y] = position
      let step = nextLen / (VALUE_INTERVAL - 1)
      let mainImgData = this.getLineImageData(len, direction, BORDER_STYLE)
      let splitImgData = this.getLineImageData(len, direction, SPLIT_LINE_STYLE)
      let axisTickImgData = this.getLineImageData(SUB_AXIS_SIZE, direction, BORDER_STYLE)
      let lineImage = mainImgData

      for (let i = 0; i < VALUE_INTERVAL; i++) {
        let point = [x, y]
        point[nextDirection] += i * step
        cacheCtx.putImageData(
          lineImage,
          ...point
        )

        point[direction] += len
        cacheCtx.putImageData(
          axisTickImgData,
          ...point
        )

        lineImage = splitImgData
      }

      let axisPosition = [x, y]
      axisPosition[direction] += len
      cacheCtx.putImageData(
        this.getLineImageData(nextLen, nextDirection, BORDER_STYLE),
        ...axisPosition
      )

      return axisTickImgData
    },

    /**
     * 获取线条的imageData数据
     * @param len<Number>          imageData的横向或竖向的尺寸
     * @param direction<Number>     方向: 0.横向, 1.竖向
     * @param color<Array<Number>>  颜色数组
     */
    getLineImageData (len, direction, color) {
      let size = new Array(2)
      size[direction] = len
      size[direction ^ 1] = 1

      let imageData = new ImageData(size[0], size[1])
      let imageColorData = imageData.data

      for (let i = 0; i < len; i++) {
        let index = i << 2
        imageColorData[index] = color[0]
        imageColorData[index + 1] = color[1]
        imageColorData[index + 2] = color[2]
        imageColorData[index + 3] = 255
      }

      return imageData
    },

    /**
     * 获取虚线的imageData数据
     * @param len<Number>          imageData的横向或竖向的尺寸
     * @param direction<Number>     方向: 0.横向, 1.竖向
     * @param color<Array<Number>>  颜色数组
     */
    getDashedLineImageData (len, direction, color) {
      const LINE_STEP = 7
      const MAX_MOD = LINE_STEP - 1

      let size = new Array(2)
      size[direction] = len
      size[direction ^ 1] = 1

      let imageData = new ImageData(size[0], size[1])
      let imageColorData = imageData.data

      for (let i = 0; i < len; i++) {
        let mod = (i + 1) % LINE_STEP

        if (mod === 0 || mod === MAX_MOD) {
          continue
        }

        let index = i << 2
        imageColorData[index] = color[0]
        imageColorData[index + 1] = color[1]
        imageColorData[index + 2] = color[2]
        imageColorData[index + 3] = 255
      }

      return imageData
    },

    /**
     * 画出yAxis的name
     * @param ctx
     */
    drawYAxisName (ctx, left) {
      let {yAxis: {name, unit}} = this.chartOpt
      ctx.font = AXIS_FONT
      ctx.textBaseline = 'top'
      ctx.textAlign = 'center'
      ctx.fillStyle = TEXT_FILL_STYLE
      ctx.fillText(`${name}${unit ? `(${unit})` : ''}`, left, BASE_TOP)
    },

    /**
     * 绘制xyChart的刻度值
     * @param cacheCtx<CanvasRenderingContext2D>  缓冲区画布的画笔
     * @param position<Array<Number>>             起始位置  [x, y]
     * @param len<Number>                         长度
     * @param align<String>                       对齐方式  [align1, align2, align3]
     * @param direction<Number>                   方向: 1.横向  2.纵向
     */
    drawAxisLabel (cacheCtx, position, len, align, direction) {
      let interval = VALUE_INTERVAL - 1
      let step = len / interval
      let speedStep = MAX_SPEED / interval
      let textStyle = ['textAlign', 'textBaseline']
      let otherDirection = direction ^ 1

      cacheCtx.font = AXIS_FONT
      cacheCtx.fillStyle = TEXT_FILL_STYLE
      cacheCtx[textStyle[otherDirection]] = align[0]
      cacheCtx[textStyle[direction]] = align[1]
      position[otherDirection] += SUB_AXIS_SIZE + SIDE_CHART_AXIS_GAP

      for (let i = 0; i < interval; i++) {
        cacheCtx.fillText(Math.round(i * speedStep), position[0], position[1])
        position[direction] += step
        cacheCtx[textStyle[direction ^ 1]] = align[2]
      }
    },

    /**
     * 画风廓线雷达图
     */
    drawDisplayChart () {
      let {cacheChart} = this
      let displayChart = this.getDisplayChart()
      let ctx = displayChart.getContext('2d')
      displayChart.width = this.chartWidth
      displayChart.height = this.chartHeight

      this.drawDisplayLegend(ctx, cacheChart)
      this.drawDisplaySpectra(ctx, cacheChart)
      this.drawDisplayYChart(ctx, cacheChart)
      this.drawDisplayXChart(ctx, cacheChart)
      this.drawDisplayXYAxis(ctx, cacheChart)
      this.drawYAxisName(ctx, LEGEND_WIDTH + this.displaySpectraWidth + Math.round(this.yAxisWidth) / 2)
    },

    drawDisplayLegend (ctx, cacheChart) {
      let totalLegendHeight = TOP_GAP + LEGEND_HEIGHT
      ctx.drawImage(
        cacheChart,
        0, 0,
        LEGEND_WIDTH, totalLegendHeight,
        0, 0,
        LEGEND_WIDTH, totalLegendHeight
      )
    },

    drawDisplaySpectra (ctx, cacheChart) {
      let {displaySpectraWidth, displaySpectraHeight} = this
      ctx.clearRect(LEGEND_WIDTH, TOP_GAP, displaySpectraWidth, displaySpectraHeight)
      ctx.drawImage(
        cacheChart,
        LEGEND_WIDTH + this.startX, TOP_GAP + this.startY,
        displaySpectraWidth, displaySpectraHeight,
        LEGEND_WIDTH, TOP_GAP,
        displaySpectraWidth, displaySpectraHeight
      )
    },

    drawDisplayYChart (ctx, cacheChart) {
      let {rightChartWidth, displaySpectraHeight} = this
      ctx.clearRect(this.chartWidth - rightChartWidth, TOP_GAP, rightChartWidth, displaySpectraHeight)
      ctx.drawImage(
        cacheChart,
        this.cacheChartWidth - rightChartWidth, TOP_GAP + this.startY,
        rightChartWidth, displaySpectraHeight,
        this.chartWidth - rightChartWidth, TOP_GAP,
        rightChartWidth, displaySpectraHeight
      )
    },

    drawDisplayXChart (ctx, cacheChart) {
      let {displaySpectraWidth} = this
      ctx.clearRect(LEGEND_WIDTH, TOP_GAP + this.displaySpectraHeight, displaySpectraWidth, BOTTOM_CHART_HEIGHT)
      ctx.drawImage(
        cacheChart,
        LEGEND_WIDTH + this.startX, this.cacheChartHeight - BOTTOM_CHART_HEIGHT,
        displaySpectraWidth, BOTTOM_CHART_HEIGHT,
        LEGEND_WIDTH, TOP_GAP + this.displaySpectraHeight,
        displaySpectraWidth, BOTTOM_CHART_HEIGHT
      )
    },

    drawDisplayXYAxis (ctx, cacheChart) {
      let {rightChartWidth} = this
      ctx.clearRect(this.chartWidth - rightChartWidth, this.chartHeight - BOTTOM_CHART_HEIGHT, rightChartWidth, BOTTOM_CHART_HEIGHT)
      ctx.drawImage(
        cacheChart,
        this.cacheChartWidth - rightChartWidth, this.cacheChartHeight - BOTTOM_CHART_HEIGHT,
        rightChartWidth, BOTTOM_CHART_HEIGHT,
        this.chartWidth - rightChartWidth, this.chartHeight - BOTTOM_CHART_HEIGHT,
        rightChartWidth, BOTTOM_CHART_HEIGHT
      )
    },

    /**
     * 判断当前坐标是否在雷达图谱上
     * @param x<Number> 横坐标
     * @param y<Number> 纵坐标
     */
    isNotInSpectra (x, y) {
      return x < LEGEND_WIDTH || x >= (LEGEND_WIDTH + this.displaySpectraWidth) || y < TOP_GAP || y >= (TOP_GAP + this.displaySpectraHeight)
    },

    /**
     * 判断当前坐标是否在xChart上
     * @param x<Number> 横坐标
     * @param y<Number> 纵坐标
     */
    isNotInXChart (x, y) {
      let {chartHeight} = this
      return x < LEGEND_WIDTH || x >= (LEGEND_WIDTH + this.displaySpectraWidth) || y < (chartHeight - X_CHART_HEIGHT) || y >= chartHeight
    },

    /**
     * 判断当前坐标是否在yChart上
     * @param x<Number> 横坐标
     * @param y<Number> 纵坐标
     */
    isNotInYChart (x, y) {
      let {chartWidth} = this
      return x < (chartWidth - Y_CHART_WIDTH) || x >= chartWidth || y < TOP_GAP || y >= (TOP_GAP + this.displaySpectraHeight)
    },

    /**
     * 称动图谱和xChart yChart
     * @param startX<Number>  将要把chart移动的起启x坐标
     * @param startY<Number>  将要把chart移动的起启y坐标
     */
    moveChartByXY (startX, startY) {
      let isMove = 3

      if (startX < 0) {
        startX = 0
      } else {
        let {displaySpectraWidth, cacheSpectraWidth} = this

        if ((startX + displaySpectraWidth) > cacheSpectraWidth) {
          startX = cacheSpectraWidth - displaySpectraWidth
        }
      }

      if (startX === this.startX) {
        isMove ^= 1
      }

      if (startY < 0) {
        startY = 0
      } else {
        let {displaySpectraHeight, cacheSpectraHeight} = this

        if ((startY + displaySpectraHeight) > cacheSpectraHeight) {
          startY = cacheSpectraHeight - displaySpectraHeight
        }
      }

      if (startY === this.startY) {
        isMove ^= 2
      }

      this.startX = startX
      this.startY = startY

      let {cacheChart} = this
      let displayChart = this.getDisplayChart()
      let ctx = displayChart.getContext('2d')

      if (isMove) {
        this.drawDisplaySpectra(ctx, cacheChart)
        this.drawSelectedChart()
      }

      if (isMove & 1) {
        this.drawDisplayXChart(ctx, cacheChart)
      }

      if (isMove & 2) {
        this.drawDisplayYChart(ctx, cacheChart)
      }

      return isMove
    },

    /**
     * 内部使用, 获取当前鼠标所在图表位置的数据
     * @param event 鼠标事件对象
     */
    getDataByEvent (event) {
      let {layerX, layerY, type} = event
      let {
        startX,
        startY,
        widthScale,
        heightScale,
        chartOpt: {
          xAxis: {
            data: xAxisData
          },
          yAxis: {
            data: yAxisData
          },
          series: {
            data: seriesData
          }
        }
      } = this

      let isNotInSpectra = this.isNotInSpectra(layerX, layerY)
      let selectedInfo = {}
      let x
      let y

      if (type === 'dblclick') {
        if (isNotInSpectra) {
          return null
        }

        x = Math.floor((layerX - LEGEND_WIDTH + startX) / widthScale)
        y = Math.floor((layerY - TOP_GAP + startY) / heightScale)

        selectedInfo.area = 3
        selectedInfo.selectedData = {
          xValues: seriesData.map(values => Array.isArray(values) ? values[y] : []),
          yValues: seriesData[x]
        }
      } else if (isNotInSpectra) {
        let currentSelectedInfo = this.selectedInfo

        if (!currentSelectedInfo) {
          return null
        }

        if (this.isNotInXChart(layerX, layerY)) {
          if (this.isNotInYChart(layerX, layerY)) {
            return null
          }

          x = currentSelectedInfo.x
          y = Math.floor((layerY - TOP_GAP + startY) / heightScale)
          selectedInfo.area = 2
        } else {
          x = Math.floor((layerX - LEGEND_WIDTH + startX) / widthScale)
          y = currentSelectedInfo.y
          selectedInfo.area = 1
        }
      } else {
        selectedInfo.area = 3
        x = Math.floor((layerX - LEGEND_WIDTH + startX) / widthScale)
        y = Math.floor((layerY - TOP_GAP + startY) / heightScale)
      }

      selectedInfo.x = x
      selectedInfo.y = y
      selectedInfo.xData = xAxisData[x]
      selectedInfo.yData = yAxisData[y]
      selectedInfo.value = seriesData[x][y]

      return selectedInfo
    },

    /**
     * 解发移动事件
     */
    triggerChartMoved (xDiff, yDiff) {
      let {
        chartOpt: {
          xAxis: {
            data: xAxisData
          },
          yAxis: {
            data: yAxisData
          }
        },
        startX,
        startY,
        widthScale,
        heightScale,
        displaySpectraWidth,
        displaySpectraHeight
      } = this
      let beginX = Math.floor(startX / widthScale)
      let beginY = Math.floor(startY / heightScale)
      let endX = Math.ceil((startX + displaySpectraWidth - widthScale) / widthScale)
      let endY = Math.ceil((startY + displaySpectraHeight - heightScale) / heightScale)
      this.$emit(WIND_PROFILE_CHART_MOVED, {
        beginXAxisIndex: beginX,
        beginXAxisValue: xAxisData[beginX],
        beginYAxisIndex: beginY,
        beginYAxisValue: yAxisData[beginY],
        endXAxisIndex: endX,
        endXAxisValue: xAxisData[endX],
        endYAxisIndex: endY,
        endYAxisValue: yAxisData[endY],
        directX: (xDiff >> -1) | 1,
        directY: (yDiff >> -1) | 1
      })
    },

    /**
     * 移动图表
     * @param event 鼠标移动事件对像
     */
    moveChart (event) {
      let {layerX, layerY} = event
      let xDiff = layerX - this.oX
      let yDiff = layerY - this.oY
      let startX = this.sX - xDiff
      let startY = this.sY - yDiff
      this.moveChartByXY(startX, startY)
      this.triggerChartMoved(xDiff, yDiff)
    },

    /**
     * 根怕鼠标所在图表上的坐标显示对应的数据信息
     * @param event 鼠标事件对象
     */
    showTooltip (event) {
      let {chartOpt} = this

      if (!chartOpt) {
        return
      }

      let data = this.getDataByEvent(event, true)

      let {tooltip} = this

      if (!data) {
        tooltip.visibility = 'hidden'
        this.clearTooltipLine()
        return
      }

      let {chartOpt: {tooltip: tooltipOpt}} = this

      let {x, y, area} = data
      let tooltipLineCtx = this.clearTooltipLine()
      let tooltipData = []
      let position = [x, y]

      if (area & 1) {
        tooltipData.push({
          label: '时间',
          value: tooltipOpt.xAxisFormatter(data.xData)
        })
        this.drawTooltipLine(tooltipLineCtx, position, 1)
      }

      if (area & 2) {
        let {yAxis} = chartOpt
        tooltipData.push({
          label: yAxis.name,
          value: tooltipOpt.yAxisFormatter(data.yData)
        })
        this.drawTooltipLine(tooltipLineCtx, position, 0)
      }

      let {value: [speed, angle] = []} = data
      let color

      if (Number.isFinite(speed)) {
        let speedLevel = getSpeedLevel(speed)
        speed = tooltipOpt.speedFormatter(speed)
        color = windProfileConfigs[speedLevel].rgb
      } else {
        speed = '--'
        color = [228, 231, 237]
      }

      tooltipData.push({
        label: '风速',
        value: speed
      })

      tooltipData.push({
        label: '风向',
        value: Number.isFinite(angle) ? tooltipOpt.angleFormatter(angle) : '--'
      })

      tooltip.legendColor = [...color]

      let {layerX, layerY} = event
      tooltip.data = tooltipData
      tooltip.position = {
        x: layerX + 10,
        y: layerY + 10
      }
      tooltip.visibility = 'visible'
    },

    clearTooltipLine () {
      let tooltipCanvas = this.getTooltipLineCanvas()
      let ctx = tooltipCanvas.getContext('2d')
      ctx.clearRect(0, 0, this.chartWidth, this.chartHeight)
      tooltipCanvas.width = this.chartWidth
      tooltipCanvas.height = this.chartHeight
      return ctx
    },

    /**
     * 画出辅助线
     * @param tooltipLineCtx  辅助线画笔
     * @param position        辅助线位置
     * @param direction       方向: 0.横向, 1纵向
     */
    drawTooltipLine (tooltipLineCtx, position, direction) {
      let scaleSize = [this.widthScale, this.heightScale]
      let displaySpectraSize = [this.displaySpectraWidth, this.displaySpectraHeight]
      let startPosition = [this.startX, this.startY]
      let gapSize = [LEGEND_WIDTH, TOP_GAP]
      let otherDirection = direction ^ 1
      let scale = scaleSize[otherDirection]
      let gap = gapSize[otherDirection]
      let coord = position[otherDirection] * scale + Math.round(scale / 2) - startPosition[otherDirection] + gap

      if (coord >= gap && coord <= (gap + displaySpectraSize[otherDirection])) {
        let linePosition = new Array(2)
        linePosition[direction] = gapSize[direction]
        linePosition[otherDirection] = coord

        tooltipLineCtx.putImageData(
          this.getDashedLineImageData(displaySpectraSize[direction], direction, WIND_PROFILE_TOOLTIP_BORDER_STYLE),
          linePosition[0], linePosition[1]
        )

        if (this.selectedInfo) {
          let chartSize = [this.chartWidth, this.chartHeight]
          let subChartSize = [X_CHART_HEIGHT, Y_CHART_WIDTH]
          let lineLen = subChartSize[otherDirection]
          linePosition[direction] = chartSize[direction] - lineLen

          tooltipLineCtx.putImageData(
            this.getDashedLineImageData(lineLen, direction, WIND_PROFILE_TOOLTIP_BORDER_STYLE),
            linePosition[0], linePosition[1]
          )
        }
      }
    },

    /**
     * 清除选中数据后的辅助线缓冲区
     */
    clearSelectedCache () {
      let {cacheSpectraWidth, cacheSpectraHeight} = this
      let canvas = this.cacheSelectedChart
      let ctx = canvas.getContext('2d')
      canvas.width = cacheSpectraWidth
      canvas.height = cacheSpectraHeight
      ctx.clearRect(0, 0, cacheSpectraWidth, cacheSpectraHeight)
      return ctx
    },

    /**
     * 建立选中数据后的辅助线缓冲区
     */
    buildSelectedCache (ctx, selectedInfo) {
      let {widthScale, heightScale} = this
      ctx.putImageData(
        this.getLineImageData(this.cacheSpectraHeight, 1, WIND_PROFILE_TOOLTIP_BORDER_STYLE),
        selectedInfo.x * widthScale + Math.round(widthScale / 2), 0
      )
      ctx.putImageData(
        this.getLineImageData(this.cacheSpectraWidth, 0, WIND_PROFILE_TOOLTIP_BORDER_STYLE),
        0, selectedInfo.y * heightScale + Math.round(heightScale / 2)
      )
    },

    /**
     * 清除选中数据的辅助线
     */
    clearSelectedChart () {
      let {chartWidth, chartHeight} = this
      let selectedCanvas = this.getSelectedCanvas()
      let ctx = selectedCanvas.getContext('2d')

      ctx.clearRect(0, 0, chartWidth, chartHeight)
      selectedCanvas.width = chartWidth
      selectedCanvas.height = chartHeight
      return ctx
    },

    /**
     * 绘制选中数据后的输助线
     */
    drawSelectedChart () {
      let ctx = this.clearSelectedChart()
      let {displaySpectraWidth, displaySpectraHeight} = this
      ctx.drawImage(
        this.cacheSelectedChart,
        this.startX, this.startY,
        displaySpectraWidth, displaySpectraHeight,
        LEGEND_WIDTH, TOP_GAP,
        displaySpectraWidth, displaySpectraHeight
      )
    },

    /**
     * 清除趋势图缓冲区
     */
    clearTrendCache () {
      return [{
        chart: this.cacheXTrendChart,
        size: [this.cacheSpectraWidth, X_CHART_HEIGHT]
      }, {
        chart: this.cacheYTrendChart,
        size: [Y_CHART_WIDTH, this.cacheSpectraHeight]
      }].map(({chart, size: [width, height]}, index) => {
        let ctx = chart.getContext('2d')
        ctx.clearRect(0, 0, width, height)
        chart.width = width
        chart.height = height
        return ctx
      })
    },

    /**
     * 创建趋势图缓冲区
     */
    buildTrendCache (cacheCtxList, selectedInfo, cacheCtx) {
      let {selectedData} = selectedInfo
      let selectedValues = [selectedData.xValues, selectedData.yValues]
      let chartSize = [X_CHART_HEIGHT, Y_CHART_WIDTH]
      let scales = [this.widthScale, this.heightScale]
      let positions = [
        [LEGEND_WIDTH, this.cacheChartHeight - X_CHART_HEIGHT + 1],
        [this.cacheChartWidth - Y_CHART_WIDTH + 1, TOP_GAP]
      ]

      cacheCtxList.forEach((ctx, direction) => {
        let otherDirection = direction ^ 1
        let values = selectedValues[direction]
        let scale = scales[direction]
        let halfScale = Math.round(scale / 2)
        let ratioSize = chartSize[direction]
        let grdConfig = new Array(2)
        grdConfig[direction] = 0
        grdConfig[otherDirection] = ratioSize
        let grd = ctx.createLinearGradient(0, 0, grdConfig[0], grdConfig[1])

        windProfileConfigs.forEach(({speed, rgb}) => {
          if (speed > MAX_SPEED) {
            return
          }

          grd.addColorStop(speed / MAX_SPEED, `rgb(${rgb.join(',')})`)
        })

        let method = 'moveTo'
        ratioSize -= LINE_WIDTH

        ctx.beginPath()
        ctx.imageSmoothingEnabled = true
        ctx.imageSmoothingQuality = 'high'
        ctx.lineWidth = LINE_WIDTH
        ctx.strokeStyle = grd

        values.forEach(([speed], index) => {
          if (!Number.isFinite(speed)) {
            ctx.stroke()
            ctx.closePath()
            ctx.beginPath()
            method = 'moveTo'
            return
          }

          let convertVal = Math.max(speed, 0)
          let ratio = Math.min(convertVal / MAX_SPEED, 1)
          let point = new Array(2)
          point[direction] = index * scale + halfScale
          point[otherDirection] = ratio * ratioSize
          ctx[method](point[0], point[1])
          method = 'lineTo'
        })

        ctx.stroke()
        ctx.closePath()

        let cacheTrendChart = ctx.canvas
        let {width, height} = cacheTrendChart
        let position = positions[direction]

        cacheCtx.drawImage(
          cacheTrendChart,
          0, 0,
          width, height,
          position[0], position[1],
          width, height
        )

        cacheTrendChart.width = cacheTrendChart.height = 0
      })
    },

    buildSelectedDataCache (selectedInfo, cacheCtx) {
      let selectedCacheCtx = this.clearSelectedCache()
      let trendCacheCtxList = this.clearTrendCache()
      cacheCtx = this.buildAxisCache(cacheCtx)

      if (selectedInfo) {
        this.selectedInfo = selectedInfo
        this.buildSelectedCache(selectedCacheCtx, selectedInfo)
        this.buildTrendCache(trendCacheCtxList, selectedInfo, cacheCtx)
      }
    },

    drawSelected (selectedInfo) {
      let {cacheChart} = this
      let ctx = this.getDisplayChart().getContext('2d')

      this.buildSelectedDataCache(selectedInfo)
      this.drawSelectedChart()
      this.drawDisplayXChart(ctx, cacheChart)
      this.drawDisplayYChart(ctx, cacheChart)
    },

    addData (chartDataOpt, direction) {
      // let {chartOpt, cacheChart, cacheSpectraHeight} = this
      // let {
      //   xAxis,
      //   yAxis: {
      //     data: yAxisData
      //   },
      //   series
      // } = chartOpt
      // let {
      //   xAxis: {
      //     data: xAxisData
      //   },
      //   series: {
      //     data: seriesData
      //   }
      // } = chartDataOpt
      // let tmpCtx = cacheChart.getContext('2d')
      // let {width, height} = cacheChart
      // let oldImageData = tmpCtx.getImageData(LEGEND_WIDTH, TOP_GAP, width - LEGEND_WIDTH, height - TOP_GAP)
    },

    onChartMouseDown (event) {
      let {layerX, layerY} = event

      if (this.isNotInSpectra(layerX, layerY)) {
        return
      }

      this.oX = layerX
      this.oY = layerY
      this.sX = this.startX
      this.sY = this.startY
      this.isMoving = true
    },

    onChartMouseMove (event) {
      if (this.isMoving) {
        this.tooltip.visibility = 'hidden'
        this.clearTooltipLine()
        this.moveChart(event)
      } else {
        this.showTooltip(event)
      }
    },

    onChartMouseUp (event) {
      this.isMoving = false
    },

    onChartMouseOut (event) {
      if (this.isMoving) {
        this.onChartMouseUp()
      } else {
        this.tooltip.visibility = 'hidden'
        this.clearTooltipLine()
      }
    },

    onChartMouseWheel (event) {
      let {layerX, layerY} = event

      if (this.isNotInSpectra(layerX, layerY)) {
        return
      }

      let {deltaX, deltaY} = event
      this.tooltip.visibility = 'hidden'
      this.clearTooltipLine()
      this.moveChartByXY(this.startX + deltaX, this.startY + deltaY)
      this.triggerChartMoved(-deltaX, -deltaY)
      event.preventDefault()
    },

    onChartDBLClick (event) {
      let data = this.getDataByEvent(event)

      if (data) {
        this.drawSelected(data)
        this.$emit(WIND_PROFILE_CHART_DATA_SELECTED, data)
      }
    },

    onChartContextMenu (event) {
      let {selectedInfo} = this

      if (!selectedInfo) {
        return
      }

      this.drawSelected(this.selectedInfo = null)
      this.$emit(WIND_PROFILE_CHART_DATA_CANCEL_SELECT, selectedInfo)
    },

    /**
     * 点击下载按钮时的事件
     */
    onDownloadBtnClick () {
      let {cacheChart, selectedInfo, cacheSelectedChart} = this
      let rightChartWidth = Y_CHART_WIDTH + SUB_AXIS_SIZE
      let bottomChartHeight = X_CHART_HEIGHT + MAIN_AXIS_SIZE
      let width = cacheChart.width - rightChartWidth
      let height = cacheChart.height - bottomChartHeight
      let yAxisLabelLeft = cacheChart.width - this.rightChartWidth

      if (selectedInfo) {
        width += rightChartWidth
        height += bottomChartHeight
        yAxisLabelLeft = width - Y_CHART_WIDTH - Math.round(this.yAxisWidth / 2)
      }

      let downloadCanvas = document.createElement('canvas')
      downloadCanvas.width = width
      downloadCanvas.height = height
      let downloadCtx = downloadCanvas.getContext('2d')
      downloadCtx.fillStyle = '#ffffff'
      downloadCtx.fillRect(0, 0, width, height)
      downloadCtx.drawImage(
        cacheChart,
        0, 0,
        width, height,
        0, 0,
        width, height
      )

      let cacheSpectraWidth = cacheSelectedChart.width
      let cacheSpectraHeight = cacheSelectedChart.height

      downloadCtx.drawImage(
        this.cacheSelectedChart,
        0, 0,
        cacheSpectraWidth, cacheSpectraHeight,
        LEGEND_WIDTH, TOP_GAP,
        cacheSpectraWidth, cacheSpectraHeight
      )

      this.drawYAxisName(downloadCtx, yAxisLabelLeft)

      downloadCanvas.toBlob(blob => {
        let url = URL.createObjectURL(blob)
        let link = document.createElement('a')
        link.download = `风廓线雷达图.${/\/([^/]+)$/.exec(blob.type)[1]}`
        link.href = url
        link.click()
        URL.revokeObjectURL(url)
        downloadCanvas.width = downloadCanvas.height = 0
      })
    },

    /**
     * 内部使用, 清除窗口尺寸变化的事件延迟
     */
    clearResizeTimer () {
      clearTimeout(this.windowResizeTimer)
    },

    /**
     * 内部使用, 当窗口尺寸发生变化时的事件
     */
    onWindowResize () {
      this.clearResizeTimer()
      this.windowResizeTimer = setTimeout(this.resize, 100)
    },

    /**
     * 内部使用, 获取当前图表画布
     * @returns {Vue | Element | Vue[] | Element[]}
     */
    getDisplayChart () {
      return this.$refs['wind-profile-chart']
    },

    getTooltipLineCanvas () {
      return this.$refs['tooltip-canvas']
    },

    getSelectedCanvas () {
      return this.$refs['selected-canvas']
    }
  },
  created () {
    this.cacheChart = document.createElement('canvas')
    this.cacheXTrendChart = document.createElement('canvas')
    this.cacheYTrendChart = document.createElement('canvas')
    this.cacheSelectedChart = document.createElement('canvas')
    window.addEventListener('resize', this.onWindowResize, true)
  },
  mounted () {
    this.$nextTick(() => {
      this.$emit(WIND_PROFILE_CHART_READY, this)
    })
  },
  beforeDestroy () {
    this.$emit(WIND_PROFILE_CHART_DESTROY, this)
    this.clear()
    this.clearResizeTimer()
    window.removeEventListener('resize', this.onWindowResize, true)
  }
}
</script>

<style lang="scss" scoped>
.wind-profile-chart-wrapper {
  position: relative;
  width: 100%;
  height: 100%;

  > {
    .wind-profile-chart-inner {
      position: relative;
    }

    .tooltip {
      position: absolute;
      font-size: .875rem;
      min-width: 125px;
      /*width: 175px;*/
      padding: 5px 10px;
      line-height: 1.5;
      border: 1px solid #DCDFE6 {
        radius: 3px;
      };
      color: #606266;
      white-space: nowrap;
      background-color: #ffffff;
      box-shadow: 0 0 6px 1px rgba(0, 0, 0, .075);
      user-select: none;
      z-index: 2;

      .legend-marker {
        display: inline-block;
        width: 10px;
        height: 10px;
        border-radius: 50%;
        margin-right: 4px;
        vertical-align: middle;
      }

      .tooltip-value {
        float: right;
        text-indent: 1em;
      }
    }

    .download-btn {
      position: absolute;
      font-size: 13px;
      top: 0;
      right: 10px;
      color: #606266;
      cursor: pointer;

      &:hover {
        color: #409EFF;
        text-decoration: underline;
      }
    }
  }

  .tooltip-canvas,
  .selected-canvas {
    position: absolute;
    top: 0;
    left: 0;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/Data/Charts/WindProfileChart/index.vue